<template>
  <div class="base-radio-checkbox">
    <label :for="id" class="base-radio-checkbox__container" :class="{'label-html': labelHtml}">
      <input
        v-model="proxyModel"
        :value="value"
        class="base-radio-checkbox__input"
        v-bind="mergedAttrs"
        :aria-labelledby="`${id}-label`">
      <span
        v-if="labelHtml"
        :id="`${id}-label`"
        :class="[{ 'sr-only': hideLabel }]"
        v-html="labelHtmlText"></span>
      <span v-else :id="`${id}-label`" :class="['body-smaller-semi-bold', { 'sr-only': hideLabel }]">{{ labelText }}</span>
    </label>
    <div
      v-show="errors.length"
      :id="`${id}-error`"
      :data-pw="`${playwright}-error`"
      class="body-smaller base-radio-checkbox__error">
      <oie-base-icon icon="warning" size="xs"></oie-base-icon>
      <span v-for="error in errors" :key="error"> {{ error }}</span>
    </div>
    <div
      v-show="description"
      :id="`${id}-description`"
      class="body-smaller"
      v-html="description">
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
export default {
  model: {
    prop: 'modelValue',
    event: 'change'
  },
  props: {
    labelHtml: {
      type: Boolean,
      default: false
    },
    modelValue: {
      type: [String, Boolean, Array, Object],
      required: false,
      default: null
    },
    value: {
      type: [String, Boolean, Array, Object],
      required: false,
      default: null
    },
    label: {
      type: String,
      default: ''
    },
    required: {
      type: Boolean,
      default: false
    },
    errors: {
      type: Array,
      default: () => []
    },
    description: {
      type: String,
      default: ''
    },
    hideLabel: {
      type: Boolean,
      default: false
    },
    playwright: {
      type: String,
      default: null
    }
  },
  computed: {
    id() {
      return this.$attrs.id || _.uniqueId('base-radio-checkbox-')
    },
    inputType() {
      return _.isArray(this.modelValue) || _.isBoolean(this.modelValue) ? 'checkbox' : 'radio'
    },
    labelText() {
      return this.required ? `${this.label}*` : this.label
    },
    labelHtmlText() {
      return this.required ? `${this.label}` : this.label
    },
    mergedAttrs() {
      return {
        id: this.id,
        type: this.inputType,
        'aria-required': this.required ? 'true' : 'false',
        'aria-invalid': this.errors.length ? 'true' : 'false',
        'aria-describedby': `${this.id}-error ${this.id}-description`,
        'data-pw': this.playwright || null,
        ...this.$attrs
      }
    },
    proxyModel: {
      get() {
        return this.modelValue
      },
      set(value) {
        this.$emit('change', value)
      }
    }
  }
}
</script>

<style lang="scss">

// General
.base-radio-checkbox {
  display: flex;
  flex-direction: column;
  gap: measure(quarter);
}

.base-radio-checkbox__container {
  display: flex;
  align-items: center;
  gap: measure(quarter);
  &.label-html {
    align-items: flex-start;
    margin-top: 0.75rem;
  }
}
.base-radio-checkbox__container.label-html p {
  margin-bottom: 0.75rem;
}
.base-radio-checkbox__container.label-html p a {
  color: color(white);
  text-decoration: underline;
}
.base-radio-checkbox__input {
  height: 24px;
  width: 24px;
  accent-color: color(brand-blue-2);
  flex-shrink: 0;
}

// Error styles
.base-radio-checkbox__input[aria-invalid='true'] {
  accent-color: color(text-red);
}

.base-radio-checkbox__error {
  display: flex;
  align-items: center;
  gap: measure(eighth);
  flex-wrap: wrap;
  color: color(text-red);
}
</style>
